import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Users,
  Building2,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  Filter,
  Upload,
  Eye,
  Ban,
  Play,
  Calendar,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Plus,
  X,
  Download,
  Bell,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
    pending_review: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    terminated: 'bg-red-700/20 text-red-500 border-red-700/30',
    valid: 'bg-green-500/20 text-green-400 border-green-500/30',
    expired: 'bg-red-500/20 text-red-400 border-red-500/30',
    expiring: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.inactive}`}>
      {status?.replace('_', ' ').toUpperCase()}
    </span>
  );
};

// Entity card component
const EntityCard = ({ entity, onAction, onViewCredentials }) => {
  const isDriver = entity.entity_type === 'driver';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-slate-600 transition-colors"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDriver ? 'bg-cyan-500/20' : 'bg-purple-500/20'}`}>
            {isDriver ? <Users className="w-5 h-5 text-cyan-400" /> : <Building2 className="w-5 h-5 text-purple-400" />}
          </div>
          <div>
            <h4 className="font-semibold text-white">{entity.name || entity.entity_name || 'Unknown'}</h4>
            <p className="text-sm text-slate-400 font-mono">{entity.entity_id}</p>
          </div>
        </div>
        <StatusBadge status={entity.status || entity.compliance_status} />
      </div>

      {/* Credentials Summary */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="bg-slate-900/50 rounded-lg p-2">
          <p className="text-lg font-bold text-green-400">{entity.valid_credentials || 0}</p>
          <p className="text-xs text-slate-500">Valid</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-2">
          <p className="text-lg font-bold text-amber-400">{entity.expiring_soon || 0}</p>
          <p className="text-xs text-slate-500">Expiring</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-2">
          <p className="text-lg font-bold text-red-400">{entity.expired_credentials || 0}</p>
          <p className="text-xs text-slate-500">Expired</p>
        </div>
      </div>

      {/* Next Expiry */}
      {entity.next_expiry && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <Calendar className="w-4 h-4" />
          <span>Next expiry: {new Date(entity.next_expiry).toLocaleDateString()}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onViewCredentials(entity)}
          className="flex-1 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white text-sm transition-colors flex items-center justify-center gap-1"
        >
          <Eye className="w-4 h-4" />
          View
        </button>
        {entity.status !== 'suspended' && entity.status !== 'terminated' ? (
          <button
            onClick={() => onAction(entity, 'suspend')}
            className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm transition-colors flex items-center justify-center gap-1"
          >
            <Ban className="w-4 h-4" />
            Suspend
          </button>
        ) : (
          <button
            onClick={() => onAction(entity, 'activate')}
            className="flex-1 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 text-sm transition-colors flex items-center justify-center gap-1"
          >
            <Play className="w-4 h-4" />
            Activate
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Main Credential Manager Component
export const CredentialManager = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [expiringCredentials, setExpiringCredentials] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [syncRes, entitiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/exchange/dispatch/sync/status`),
        axios.get(`${API_URL}/api/exchange/dispatch/dispatch-drivers`)
      ]);
      
      setSyncStatus(syncRes.data);
      
      // Transform drivers to entities format
      const drivers = (entitiesRes.data.drivers || []).map(d => ({
        ...d,
        entity_type: 'driver',
        entity_id: d.driver_id,
        entity_name: d.name || d.full_name,
        status: d.status,
        valid_credentials: Math.floor(Math.random() * 5) + 3, // Mock for now
        expiring_soon: Math.floor(Math.random() * 2),
        expired_credentials: d.status === 'suspended' ? 1 : 0,
      }));
      
      setEntities(drivers);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load credential data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle entity action (suspend/activate)
  const handleEntityAction = async () => {
    if (!actionModal || !actionReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setProcessing(true);
    try {
      const endpoint = actionModal.entity.entity_type === 'company' 
        ? `/api/exchange/dispatch/company/${actionModal.entity.entity_id}/${actionModal.action}`
        : `/api/exchange/dispatch/trigger/${actionModal.action === 'suspend' ? 'expire' : 'renew'}-credential`;

      const params = actionModal.entity.entity_type === 'company'
        ? `?reason=${encodeURIComponent(actionReason)}`
        : `?driver_id=${actionModal.entity.entity_id}&credential_type=compliance&reason=${encodeURIComponent(actionReason)}`;

      await axios.post(`${API_URL}${endpoint}${params}`);
      
      toast.success(`${actionModal.entity.entity_type} ${actionModal.action}ed successfully`);
      setActionModal(null);
      setActionReason('');
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${actionModal.action} entity`);
    } finally {
      setProcessing(false);
    }
  };

  // Filter entities
  const filteredEntities = entities.filter(e => {
    const matchesSearch = !searchQuery || 
      (e.entity_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (e.entity_id?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || e.entity_type === filterType;
    const matchesStatus = filterStatus === 'all' || e.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Stats
  const stats = {
    totalEntities: entities.length,
    activeEntities: entities.filter(e => e.status === 'active' || e.status === 'available').length,
    suspendedEntities: entities.filter(e => e.status === 'suspended').length,
    expiringCredentials: entities.reduce((sum, e) => sum + (e.expiring_soon || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credential-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-cyan-400" />
            Credential Management
          </h2>
          <p className="text-slate-400 mt-1">
            Manage driver and company credentials for OVA Dispatch
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`${API_URL}/api/exchange/dispatch/cron/check-expiry`);
                toast.success('Expiry check started');
              } catch (error) {
                toast.error('Failed to start expiry check');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 transition-colors"
            title="Run credential expiry check"
          >
            <Clock className="w-4 h-4" />
            Check Expiry
          </button>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <a
            href="https://ovadispatch.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            OVA Dispatch
          </a>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-cyan-400" />
            <div>
              <p className="text-slate-400 text-sm">Total Entities</p>
              <p className="text-2xl font-bold text-white">{stats.totalEntities}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400">{stats.activeEntities}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-400" />
            <div>
              <p className="text-slate-400 text-sm">Suspended</p>
              <p className="text-2xl font-bold text-red-400">{stats.suspendedEntities}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
            <div>
              <p className="text-slate-400 text-sm">Expiring Soon</p>
              <p className="text-2xl font-bold text-amber-400">{stats.expiringCredentials}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: Shield },
          { id: 'drivers', label: 'Drivers', icon: Users },
          { id: 'companies', label: 'Companies', icon: Building2 },
          { id: 'expiring', label: 'Expiring', icon: Clock },
          { id: 'sync-log', label: 'Sync Log', icon: Zap },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-cyan-500/20 text-cyan-400'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      {(activeTab === 'overview' || activeTab === 'drivers' || activeTab === 'companies') && (
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or ID..."
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-cyan-500/50 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="available">Available</option>
            <option value="suspended">Suspended</option>
            <option value="pending_review">Pending Review</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      )}

      {/* Content based on active tab */}
      {(activeTab === 'overview' || activeTab === 'drivers') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEntities.filter(e => activeTab === 'overview' || e.entity_type === 'driver').map(entity => (
            <EntityCard
              key={entity.entity_id}
              entity={entity}
              onAction={(entity, action) => setActionModal({ entity, action })}
              onViewCredentials={(entity) => setSelectedEntity(entity)}
            />
          ))}
          {filteredEntities.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              No entities found matching your criteria
            </div>
          )}
        </div>
      )}

      {activeTab === 'companies' && (
        <div className="text-center py-12 text-slate-500">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Company management coming soon.</p>
          <p className="text-sm mt-2">Waiting for OVA Dispatch to provide company data.</p>
        </div>
      )}

      {activeTab === 'expiring' && (
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            Credentials Expiring Within 30 Days
          </h3>
          {syncStatus?.driver_statuses?.filter(d => d.issues_count > 0).length > 0 ? (
            <div className="space-y-3">
              {syncStatus.driver_statuses.filter(d => d.issues_count > 0).map((driver, idx) => (
                <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-white font-medium">{driver.name}</p>
                      <p className="text-sm text-slate-500">{driver.driver_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-amber-400 text-sm">{driver.issues_count} issue(s)</span>
                    <StatusBadge status={driver.exchange_compliance} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No credentials expiring soon</p>
          )}
        </div>
      )}

      {activeTab === 'sync-log' && (
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-400" />
            Recent Sync Activity (Exchange → Dispatch)
          </h3>
          {syncStatus?.recent_syncs?.length > 0 ? (
            <div className="space-y-2">
              {syncStatus.recent_syncs.map((log, idx) => (
                <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${
                      log.action === 'suspend' ? 'bg-red-400' :
                      log.action === 'activate' ? 'bg-green-400' :
                      log.action === 'warn' ? 'bg-amber-400' : 'bg-slate-400'
                    }`} />
                    <span className="text-white font-mono">{log.driver_id || log.entity_id}</span>
                    <StatusBadge status={log.action} />
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-slate-400 max-w-xs truncate">{log.reason}</span>
                    <span className="text-slate-500 text-xs">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No sync activity yet</p>
          )}
        </div>
      )}

      {/* Action Modal */}
      <AnimatePresence>
        {actionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={() => setActionModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-xl p-6 w-full max-w-md mx-4 border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                {actionModal.action === 'suspend' ? (
                  <Ban className="w-6 h-6 text-red-400" />
                ) : (
                  <Play className="w-6 h-6 text-green-400" />
                )}
                <h3 className="text-xl font-bold text-white">
                  {actionModal.action === 'suspend' ? 'Suspend' : 'Activate'} {actionModal.entity.entity_type}
                </h3>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-3 mb-4">
                <p className="text-white font-medium">{actionModal.entity.entity_name || actionModal.entity.name}</p>
                <p className="text-sm text-slate-400 font-mono">{actionModal.entity.entity_id}</p>
              </div>

              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-2">
                  Reason <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none resize-none"
                  placeholder={actionModal.action === 'suspend' 
                    ? "e.g., Background check expired, Insurance lapsed..."
                    : "e.g., Credentials renewed, Compliance restored..."
                  }
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setActionModal(null)}
                  className="px-4 py-2 bg-white/5 text-slate-400 rounded-lg hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEntityAction}
                  disabled={processing || !actionReason.trim()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50 ${
                    actionModal.action === 'suspend'
                      ? 'bg-red-600 text-white hover:bg-red-500'
                      : 'bg-green-600 text-white hover:bg-green-500'
                  }`}
                >
                  {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {actionModal.action === 'suspend' ? 'Suspend' : 'Activate'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entity Detail Modal */}
      <AnimatePresence>
        {selectedEntity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedEntity(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl mx-4 border border-white/10 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    selectedEntity.entity_type === 'driver' ? 'bg-cyan-500/20' : 'bg-purple-500/20'
                  }`}>
                    {selectedEntity.entity_type === 'driver' 
                      ? <Users className="w-6 h-6 text-cyan-400" />
                      : <Building2 className="w-6 h-6 text-purple-400" />
                    }
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {selectedEntity.entity_name || selectedEntity.name}
                    </h3>
                    <p className="text-slate-400 font-mono">{selectedEntity.entity_id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedEntity(null)}
                  className="p-2 text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                  <span className="text-slate-400">Current Status</span>
                  <StatusBadge status={selectedEntity.status} />
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Credential Status</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-400">{selectedEntity.valid_credentials || 0}</p>
                      <p className="text-xs text-slate-500">Valid</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-400">{selectedEntity.expiring_soon || 0}</p>
                      <p className="text-xs text-slate-500">Expiring</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-400">{selectedEntity.expired_credentials || 0}</p>
                      <p className="text-xs text-slate-500">Expired</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Required Credentials</h4>
                  <div className="space-y-2">
                    {[
                      { type: "Driver's License", status: 'valid', expiry: '2028-01-15' },
                      { type: 'Auto Insurance', status: 'valid', expiry: '2026-06-01' },
                      { type: 'Background Check', status: selectedEntity.status === 'suspended' ? 'expired' : 'valid', expiry: '2025-12-01' },
                      { type: 'Drug Test', status: 'valid', expiry: '2026-03-15' },
                      { type: 'Vehicle Inspection', status: 'valid', expiry: '2026-08-20' },
                    ].map((cred, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                        <span className="text-white">{cred.type}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-sm">{cred.expiry}</span>
                          <StatusBadge status={cred.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setSelectedEntity(null)}
                  className="px-4 py-2 bg-white/5 text-slate-400 rounded-lg hover:bg-white/10"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setSelectedEntity(null);
                    setActionModal({ 
                      entity: selectedEntity, 
                      action: selectedEntity.status === 'suspended' ? 'activate' : 'suspend' 
                    });
                  }}
                  className={`px-4 py-2 rounded-lg ${
                    selectedEntity.status === 'suspended'
                      ? 'bg-green-600 text-white hover:bg-green-500'
                      : 'bg-red-600 text-white hover:bg-red-500'
                  }`}
                >
                  {selectedEntity.status === 'suspended' ? 'Activate' : 'Suspend'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CredentialManager;
